/* eslint-disable @typescript-eslint/no-explicit-any */
// WebSocket-driven replacement for the legacy polling conversation web worker.
// Subscribes to #message#{convId} for the active conversation and #conversation#
// for the conversation list, then fetches only the changed item on each event —
// no periodic polling.
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAxios } from '@/hooks/useAxios';
import { useRealtimeSubscription } from '@/hooks/useRealtimeSubscription';
import type { WebSocketMessage } from '@/hooks/useWebSocket';
import { useAuth } from '@/state/auth';
import type { Conversation, Message } from '@/types';

interface PendingMessage {
  id: string;
  text: string;
  timestamp: number;
}

interface ChatWorkerState {
  conversations: Conversation[];
  messages: Message[];
  pendingMessages: PendingMessage[];
  conversationsLoading: boolean;
  messagesLoading: boolean;
  error: string | null;
  sendMessage: (
    conversationId: string,
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<void>;
  createConversation: (
    message: string,
    mode?: 'query' | 'agent'
  ) => Promise<any>;
  compactConversation: (sourceConversationId: string) => Promise<any>;
  refetchMessages: (convId: string) => Promise<void>;
}

const PENDING_TIMEOUT_MS = 30_000;

// Extract the conversationId from a key shaped like "#message#{convId}#{msgId}".
function conversationIdFromMessageKey(key: string): string | null {
  const parts = key.split('#').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'message') {
    return parts[1];
  }
  return null;
}

// Extract the conversationId from a key shaped like "#conversation#{convId}".
function conversationIdFromConversationKey(key: string): string | null {
  const parts = key.split('#').filter(Boolean);
  if (parts.length >= 2 && parts[0] === 'conversation') {
    return parts[1];
  }
  return null;
}

function conversationMatchesId(c: Conversation, convId: string): boolean {
  return c.uuid === convId || c.id === convId || (c.key || '').includes(convId);
}

// ENG-3112: WebSocket INSERT event deduplication (no code change required).
//
// The SQS BatchItemFailures retry mechanism on the backend may redeliver the
// same DynamoDB INSERT event over the WebSocket one or more additional times.
// Deduplication is handled at two layers:
//
//   1. mergeConversation (below) and mergeMessage (below) both key on
//      `item.key` — the DynamoDB sort key. A redelivered event for an already-
//      present item lands at the same index and replaces the existing entry
//      with an identical object, producing no visible state change.
//
//   2. On the invalidation path (useRealtimeSubscription -> debouncedInvalidate
//      -> queryClient.invalidateQueries), TanStack Query's in-flight deduplication
//      prevents multiple identical in-flight fetches even if invalidate fires
//      several times in rapid succession for the same query key.
//
// No runtime deduplication guard (e.g. a Set of seen keys) is needed here;
// the merge-by-key pattern is sufficient.

function mergeConversation(
  list: Conversation[],
  updated: Conversation
): Conversation[] {
  const idx = list.findIndex(c => c.key === updated.key);
  const next =
    idx >= 0
      ? [...list.slice(0, idx), updated, ...list.slice(idx + 1)]
      : [updated, ...list];
  return next.sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
}

function mergeMessage(list: Message[], updated: Message): Message[] {
  const idx = list.findIndex(m => m.key === updated.key);
  const next =
    idx >= 0
      ? [...list.slice(0, idx), updated, ...list.slice(idx + 1)]
      : [...list, updated];
  return next.sort(
    (a, b) =>
      new Date(a.timestamp || '').getTime() -
      new Date(b.timestamp || '').getTime()
  );
}

// WS tenant arrives as the raw DDB partition: "{account}" for tenant-public
// items, "{account}:{user}" for per-user items. Strip the user suffix so the
// guard compares against the impersonated account regardless of partition shape.
function tenantAccount(tenant: string): string {
  const colon = tenant.indexOf(':');
  return colon === -1 ? tenant : tenant.slice(0, colon);
}

function pendingMatchesRealMessage(
  pending: PendingMessage,
  real: Message
): boolean {
  if (real.role !== 'user') return false;
  if (real.id === pending.id || real.uuid === pending.id) return true;
  // Fallback: content match within 5s of pending send time
  if (real.content === pending.text) {
    const realTs = new Date(real.timestamp || 0).getTime();
    if (Math.abs(realTs - pending.timestamp) < 5000) return true;
  }
  return false;
}

// Returns true for conversations that belong in the root sidebar — i.e. root
// conversations that are not hunt iterations.  Both guards are needed:
//   • parent_id check: excludes subagent conversations (non-root children)
//   • huntId check: excludes hunt iteration conversations (root but hunt-scoped)
function isSidebarConversation(conv: Conversation): boolean {
  return (!conv.parent_id || conv.parent_id === 'self') && !conv.huntId;
}

export function useChatWorker(conversationId?: string): ChatWorkerState {
  const axios = useAxios();
  const { isImpersonating, friend, me } = useAuth();
  const currentUser = isImpersonating ? friend : me;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingMessages, setPendingMessages] = useState<PendingMessage[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs avoid re-subscribing WS handlers on every state change.
  const activeConvIdRef = useRef<string | undefined>(conversationId);
  activeConvIdRef.current = conversationId;
  const pendingByConvRef = useRef<Map<string, PendingMessage[]>>(new Map());
  const pendingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const safetyNetTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const activeUserRef = useRef<string>(currentUser);
  activeUserRef.current = currentUser;

  const syncPendingForActive = useCallback(() => {
    const convId = activeConvIdRef.current;
    if (!convId) {
      setPendingMessages([]);
      return;
    }
    setPendingMessages([...(pendingByConvRef.current.get(convId) || [])]);
  }, []);

  const removePending = useCallback(
    (convId: string, messageId: string) => {
      const existing = pendingByConvRef.current.get(convId) || [];
      const remaining = existing.filter(p => p.id !== messageId);
      if (remaining.length !== existing.length) {
        pendingByConvRef.current.set(convId, remaining);
        if (activeConvIdRef.current === convId) {
          syncPendingForActive();
        }
      }
      const timer = pendingTimersRef.current.get(messageId);
      if (timer) {
        clearTimeout(timer);
        pendingTimersRef.current.delete(messageId);
      }
    },
    [syncPendingForActive]
  );

  const prunePendingForMessage = useCallback(
    (real: Message, convId: string) => {
      const existing = pendingByConvRef.current.get(convId) || [];
      const remaining = existing.filter(
        p => !pendingMatchesRealMessage(p, real)
      );
      if (remaining.length !== existing.length) {
        // Clear any timers for pruned entries
        for (const p of existing) {
          if (!remaining.includes(p)) {
            const timer = pendingTimersRef.current.get(p.id);
            if (timer) {
              clearTimeout(timer);
              pendingTimersRef.current.delete(p.id);
            }
          }
        }
        pendingByConvRef.current.set(convId, remaining);
        if (activeConvIdRef.current === convId) {
          syncPendingForActive();
        }
      }
    },
    [syncPendingForActive]
  );

  const fetchConversations = useCallback(async () => {
    try {
      const response = await axios.get('/my', {
        params: { label: 'conversation', key: 'parent_id:self', user: 'true' },
      });
      const list = (
        (response.data.conversations || []) as Conversation[]
      ).filter(isSidebarConversation);
      // Functional update: merge rather than replace so a concurrent WS-driven
      // mergeConversation isn't overwritten by a slightly-stale fetch snapshot.
      setConversations(prev => {
        const byKey = new Map(prev.map(c => [c.key, c]));
        for (const c of list) {
          byKey.set(c.key, c);
        }
        return [...byKey.values()].sort(
          (a, b) =>
            new Date(b.created).getTime() - new Date(a.created).getTime()
        );
      });
      setConversationsLoading(false);
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to fetch conversations'
      );
      setConversationsLoading(false);
    }
  }, [axios]);

  const fetchAndMergeConversation = useCallback(
    async (convId: string) => {
      const startUser = activeUserRef.current;
      try {
        const response = await axios.get('/my', {
          params: {
            label: 'conversation',
            key: `#conversation#${convId}`,
            user: 'true',
          },
        });
        if (activeUserRef.current !== startUser) return; // tenant flipped during fetch — drop stale
        const list = (response.data.conversations || []) as Conversation[];
        if (list.length === 0) {
          setConversations(prev =>
            prev.filter(c => !conversationMatchesId(c, convId))
          );
          return;
        }
        const conv = list[0];
        if (!isSidebarConversation(conv)) return;
        setConversations(prev => mergeConversation(prev, conv));
      } catch (err) {
        console.error('Failed to fetch conversation', convId, err);
      }
    },
    [axios]
  );

  const fetchMessages = useCallback(
    async (convId: string) => {
      try {
        setMessagesLoading(true);
        const response = await axios.get('/my', {
          params: {
            label: 'message',
            key: `#message#${convId}`,
            user: 'true',
          },
        });
        const list = (response.data.messages || []) as Message[];
        if (activeConvIdRef.current !== convId) {
          // Conversation switched mid-flight — drop the result.
          return;
        }
        // Functional update: merge fetched messages into current state rather
        // than replacing.  This prevents a concurrent WS-driven mergeMessage
        // from being overwritten by a slightly-stale fetch snapshot.
        setMessages(prev => {
          const byKey = new Map(prev.map(m => [m.key, m]));
          for (const m of list) {
            byKey.set(m.key, m);
          }
          return [...byKey.values()].sort(
            (a, b) =>
              new Date(a.timestamp || '').getTime() -
              new Date(b.timestamp || '').getTime()
          );
        });
        // Prune any pending that already made it into the list
        for (const m of list) {
          prunePendingForMessage(m, convId);
        }
        setMessagesLoading(false);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch messages'
        );
        setMessagesLoading(false);
      }
    },
    [axios, prunePendingForMessage]
  );

  // Fetch a single message by its exact key and merge into state.
  const fetchAndMergeMessage = useCallback(
    async (key: string) => {
      const convId = conversationIdFromMessageKey(key);
      if (!convId) return;
      // Only bother if this is the active conversation — otherwise the next
      // conversation switch will do a full fetch anyway.
      if (convId !== activeConvIdRef.current) return;

      try {
        const response = await axios.get('/my', {
          params: { label: 'message', key, user: 'true' },
        });
        const list = (response.data.messages || []) as Message[];
        if (activeConvIdRef.current !== convId) return;

        if (list.length === 0) {
          setMessages(prev => prev.filter(m => m.key !== key));
          return;
        }
        const updated = list[0];
        setMessages(prev => mergeMessage(prev, updated));
        prunePendingForMessage(updated, convId);
      } catch (err) {
        console.error('Failed to fetch message', key, err);
      }
    },
    [axios, prunePendingForMessage]
  );

  // Initial conversation fetch on mount.
  // Cross-tenant teardown is handled structurally by KeyedInsights' subtree
  // remount (see ui/src/app/AppRoute.tsx KeyedInsights). When navigating away
  // from Insights entirely, the route change unmounts this hook. In both cases
  // the closure is destroyed before a flushSync-window currentUser flip can
  // fire this effect, so this runs exactly once per mount.
  useEffect(() => {
    if (!currentUser) return;
    fetchConversations();
  }, []); // mount only

  // WS subscriptions are declared BEFORE the initial fetch effects so that
  // React runs the subscribe effects first on the same render cycle.  This
  // eliminates the window where a message written between fetch-response and
  // subscribe-activation would be missed.  Any duplicate delivered by WS
  // during the fetch is deduplicated by mergeMessage (keyed on item key).

  // Live message updates for the active conversation. The WS payload includes
  // the full item serialized via TableSearch (same shape as /my), so we can
  // merge directly into state without an HTTP round-trip.
  const handleMessageEvent = useCallback(
    (ws: WebSocketMessage) => {
      // Drop events before auth resolves — accepting them risks cross-tenant
      // state pollution. Initial data arrives via HTTP fetches; safety-net
      // refetches cover any WS events missed during this window.
      if (!currentUser) return;
      if (ws.tenant && tenantAccount(ws.tenant) !== currentUser) return;
      if (ws.eventName === 'REMOVE') {
        setMessages(prev => prev.filter(m => m.key !== ws.key));
        return;
      }
      const convId = conversationIdFromMessageKey(ws.key);
      if (!convId || convId !== activeConvIdRef.current) return;

      // Extract the message from the TableSearch-shaped item payload.
      const messages = (ws.item?.messages ?? []) as Message[];
      if (messages.length > 0) {
        const msg = messages[0];
        setMessages(prev => mergeMessage(prev, msg));
        prunePendingForMessage(msg, convId);
        return;
      }
      // Fallback: fetch if the item wasn't included (e.g. older backend).
      fetchAndMergeMessage(ws.key);
    },
    [fetchAndMergeMessage, prunePendingForMessage, currentUser]
  );
  useRealtimeSubscription({
    patterns: conversationId
      ? [{ pattern: `#message#${conversationId}`, matchType: 'prefix' }]
      : [],
    enabled: !!conversationId,
    onUpdate: handleMessageEvent,
  });

  // Live conversation-list updates (new conversations, status changes, deletes).
  // Only root conversations (parent_id === "self") are merged into the sidebar
  // list — subagent conversations are handled by useGetChildConversations.
  const handleConversationEvent = useCallback(
    (ws: WebSocketMessage) => {
      if (!currentUser) return;
      if (ws.tenant && tenantAccount(ws.tenant) !== currentUser) return;
      const convId = conversationIdFromConversationKey(ws.key);
      if (!convId) return;
      if (ws.eventName === 'REMOVE') {
        setConversations(prev =>
          prev.filter(c => !conversationMatchesId(c, convId))
        );
        return;
      }
      // Use the inline item if available; fall back to HTTP fetch.
      const conversations = (ws.item?.conversations ?? []) as Conversation[];
      if (conversations.length > 0) {
        const conv = conversations[0];
        if (!isSidebarConversation(conv)) return;
        setConversations(prev => mergeConversation(prev, conv));
        return;
      }
      // HTTP fallback — fetchAndMergeConversation checks parent_id internally.
      fetchAndMergeConversation(convId);
    },
    [fetchAndMergeConversation, currentUser]
  );
  useRealtimeSubscription({
    patterns: [{ pattern: '#conversation#', matchType: 'prefix' }],
    onUpdate: handleConversationEvent,
  });

  // Initial fetches run AFTER subscriptions are established above.
  useEffect(() => {
    if (conversationId) {
      setMessages([]);
      syncPendingForActive();
      fetchMessages(conversationId);
    } else {
      setMessages([]);
      setPendingMessages([]);
      setMessagesLoading(false);
    }
  }, [conversationId, fetchMessages, syncPendingForActive]);

  // Shared safety-net refetch helper. Schedules staggered fetchMessages calls
  // to catch AI responses that a WebSocket subscription timing race may have
  // missed.
  function scheduleSafetyNetRefetches(
    convId: string,
    delays: number[]
  ): ReturnType<typeof setTimeout>[] {
    const timers = delays.map(delay => {
      const timer = setTimeout(() => {
        safetyNetTimersRef.current = safetyNetTimersRef.current.filter(
          t => t !== timer
        );
        if (activeConvIdRef.current === convId) {
          void fetchMessages(convId);
        }
      }, delay);
      return timer;
    });
    safetyNetTimersRef.current.push(...timers);
    return timers;
  }

  const sendMessage = useCallback(
    async (convId: string, text: string, mode?: 'query' | 'agent') => {
      const messageId = crypto.randomUUID();
      const pending: PendingMessage = {
        id: messageId,
        text,
        timestamp: Date.now(),
      };
      const list = pendingByConvRef.current.get(convId) || [];
      const nextList = [...list, pending];
      pendingByConvRef.current.set(convId, nextList);
      if (activeConvIdRef.current === convId) {
        syncPendingForActive();
      }

      // Safety: drop stuck pending entry after timeout so the UI doesn't
      // show "sending..." forever if something goes sideways.
      const timer = setTimeout(
        () => removePending(convId, messageId),
        PENDING_TIMEOUT_MS
      );
      pendingTimersRef.current.set(messageId, timer);

      try {
        const response = await axios.post('/planner', {
          conversationId: convId,
          message: text,
          mode: mode || 'query',
          messageId,
        });
        if (!response.data?.response?.success) {
          throw new Error('Message failed to send');
        }
        scheduleSafetyNetRefetches(convId, [2_000, 5_000, 12_000]);
      } catch (err) {
        removePending(convId, messageId);
        throw err;
      }
    },
    [axios, removePending, syncPendingForActive, fetchMessages]
  );

  const createConversation = useCallback(
    async (message: string, mode?: 'query' | 'agent') => {
      const response = await axios.post('/planner', {
        conversationId: '',
        message,
        mode: mode || 'query',
      });
      if (!response.data?.conversation) {
        throw new Error('No conversation returned from server');
      }
      const conv = response.data.conversation;

      if (isSidebarConversation(conv)) {
        setConversations(prev => mergeConversation(prev, conv));
      }

      const convId =
        conv.uuid || conv.id || (conv.key || '').replace('#conversation#', '');

      if (convId) {
        // Optimistic pending message so the user's text is visible in the
        // new conversation before any WS event or safety-net refetch arrives.
        const messageId = crypto.randomUUID();
        const pending: PendingMessage = {
          id: messageId,
          text: message,
          timestamp: Date.now(),
        };
        pendingByConvRef.current.set(convId, [pending]);
        const pendingTimer = setTimeout(
          () => removePending(convId, messageId),
          PENDING_TIMEOUT_MS
        );
        pendingTimersRef.current.set(messageId, pendingTimer);

        scheduleSafetyNetRefetches(
          convId,
          [2_000, 5_000, 12_000, 20_000, 30_000]
        );
        const sidebarTimer = setTimeout(() => {
          safetyNetTimersRef.current = safetyNetTimersRef.current.filter(
            t => t !== sidebarTimer
          );
          if (activeConvIdRef.current === convId) {
            fetchAndMergeConversation(convId);
          }
        }, 3_000);
        safetyNetTimersRef.current.push(sidebarTimer);
      }

      return conv;
    },
    [axios, fetchAndMergeConversation, fetchMessages, removePending]
  );

  // Cleanup pending timers and safety-net timers on unmount.
  useEffect(() => {
    return () => {
      for (const timer of pendingTimersRef.current.values()) {
        clearTimeout(timer);
      }
      pendingTimersRef.current.clear();
      for (const timer of safetyNetTimersRef.current) {
        clearTimeout(timer);
      }
      safetyNetTimersRef.current = [];
    };
  }, []);

  // Async compaction. The server creates the target conversation synchronously
  // and enqueues an SQS event to do the actual summarization (which can take
  // longer than the API gateway's 30s window). The summary message arrives in
  // the new conversation later via the standard WebSocket fan-out.
  const compactConversation = useCallback(
    async (sourceConversationId: string) => {
      const response = await axios.post('/planner/compact', {
        conversationId: sourceConversationId,
      });
      if (!response.data?.conversation) {
        throw new Error('No conversation returned from compaction');
      }
      const conv = response.data.conversation;

      if (isSidebarConversation(conv)) {
        setConversations(prev => mergeConversation(prev, conv));
      }

      const convId =
        conv.uuid || conv.id || (conv.key || '').replace('#conversation#', '');
      if (convId) {
        // Compaction takes longer than the API timeout — refetch on a longer
        // schedule so the summary message lands without waiting for a WS event.
        scheduleSafetyNetRefetches(
          convId,
          [3_000, 8_000, 20_000, 45_000, 90_000]
        );
      }

      return conv;
    },
    [axios]
  );

  return {
    conversations,
    messages,
    pendingMessages,
    conversationsLoading,
    messagesLoading,
    error,
    sendMessage,
    createConversation,
    compactConversation,
    refetchMessages: fetchMessages,
  };
}
